(() => {
  if (window.__cahveNewProductBooted) return;
  window.__cahveNewProductBooted = true;

  const DESKTOP_QUERY = window.matchMedia('(min-width: 750px)');

  class CahveNewProduct extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cahveInitialized === 'true') return;
      this.dataset.cahveInitialized = 'true';

      this.currencyCode = this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD';
      this.isSubmitting = false;
      this.isBuyWithSubmitting = false;
      this.touchStartX = null;
      this.touchStartY = null;

      const variantScript = this.querySelector('[data-cahve-np-variants]');
      try {
        this.productData = JSON.parse(variantScript?.textContent || '{}');
      } catch (error) {
        console.error('[Cahvé new product] Invalid variant JSON.', error);
        this.productData = { variants: [] };
      }

      this.gallery = this.querySelector('[data-cahve-np-gallery]');
      this.stage = this.querySelector('[data-cahve-np-stage]');
      this.mediaItems = Array.from(this.querySelectorAll('[data-cahve-np-media-item]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-cahve-np-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-cahve-np-dot]'));
      this.currentMediaIndex = 0;

      this.lightbox = this.querySelector('[data-cahve-np-lightbox]');
      this.lightboxImage = this.querySelector('[data-cahve-np-lightbox-image]');
      this.handleLightboxKeydown = (event) => {
        if (event.key === 'Escape') this.closeZoom();
        if (event.key === 'ArrowLeft') this.showLightboxMedia(this.lightboxIndex - 1);
        if (event.key === 'ArrowRight') this.showLightboxMedia(this.lightboxIndex + 1);
      };
      this.handlePopupKeydown = (event) => {
        if (event.key === 'Escape') this.closePopup(this.querySelector('[data-cahve-np-popup].is-open'));
      };

      this.addEventListener('click', (event) => this.handleClick(event));
      this.addEventListener('change', (event) => this.handleChange(event));
      this.addEventListener(
        'scroll',
        (event) => {
          if (!event.target.matches?.('[data-cahve-np-complementary-list]')) return;
          const block = event.target.closest('[data-cahve-np-complementary]');
          window.requestAnimationFrame(() => this.updateComplementaryCarouselState(block));
        },
        { capture: true, passive: true }
      );
      this.querySelectorAll('[data-cahve-np-quantity-input]').forEach((input) => {
        input.addEventListener('input', (event) => this.handleQuantityInput(event));
      });
      this.querySelector('[data-cahve-np-form]')?.addEventListener('submit', (event) => this.handleAddToCart(event));
      this.initSelectedOptions();

      if (this.lightboxImage) {
        this.lightboxImage.addEventListener('pointerdown', (event) => this.startLightboxPointer(event));
        this.lightboxImage.addEventListener('pointermove', (event) => this.moveLightboxPointer(event));
        this.lightboxImage.addEventListener('pointerup', (event) => this.endLightboxPointer(event));
        this.lightboxImage.addEventListener('pointercancel', () => {
          this.lightboxPointer = null;
        });
      }

      if (this.stage) {
        this.stage.addEventListener('touchstart', (event) => this.handleTouchStart(event), { passive: true });
        this.stage.addEventListener('touchend', (event) => this.handleTouchEnd(event), { passive: true });
      }

      this.initRevealObserver();
      this.initTimers();
      this.initComplementary();
      this.initStickyAtc();
      this.updateVariantFromSelections();
    }

    isCarouselMode() {
      const layout = DESKTOP_QUERY.matches
        ? this.gallery?.dataset.layoutDesktop
        : this.gallery?.dataset.layoutMobile;
      return layout === 'carousel' || layout === 'carousel_thumbnails';
    }

    handleClick(event) {
      const optionButton = event.target.closest('[data-cahve-np-option-button]');
      if (optionButton) {
        event.preventDefault();
        this.selectOption(Number(optionButton.dataset.optionPosition), optionButton.dataset.optionValue);
        return;
      }

      if (event.target.closest('[data-cahve-np-prev]')) {
        event.preventDefault();
        this.showMedia(this.currentMediaIndex - 1);
        return;
      }

      if (event.target.closest('[data-cahve-np-next]')) {
        event.preventDefault();
        this.showMedia(this.currentMediaIndex + 1);
        return;
      }

      const thumb = event.target.closest('[data-cahve-np-thumb]');
      if (thumb) {
        event.preventDefault();
        this.showMedia(Number(thumb.dataset.mediaIndex));
        return;
      }

      const dot = event.target.closest('[data-cahve-np-dot]');
      if (dot) {
        event.preventDefault();
        this.showMedia(Number(dot.dataset.mediaIndex));
        return;
      }

      if (event.target.closest('[data-cahve-np-quantity-minus]')) {
        event.preventDefault();
        this.changeQuantity(-1);
        return;
      }

      if (event.target.closest('[data-cahve-np-quantity-plus]')) {
        event.preventDefault();
        this.changeQuantity(1);
        return;
      }

      if (event.target.closest('[data-cahve-np-buy-now]')) {
        event.preventDefault();
        this.submitProduct({ checkout: true, sourceButton: event.target.closest('[data-cahve-np-buy-now]') });
        return;
      }

      const accordionTrigger = event.target.closest('[data-cahve-np-accordion-trigger]');
      if (accordionTrigger) {
        event.preventDefault();
        this.toggleAccordion(accordionTrigger);
        return;
      }

      const buyWithAdd = event.target.closest('[data-cahve-np-buy-with-add]');
      if (buyWithAdd) {
        event.preventDefault();
        this.addBuyItWith(buyWithAdd);
        return;
      }

      const complementaryControl = event.target.closest('[data-cahve-np-complementary-scroll]');
      if (complementaryControl) {
        event.preventDefault();
        this.scrollComplementary(complementaryControl);
        return;
      }

      const stickyAdd = event.target.closest('[data-cahve-np-sticky-add]');
      if (stickyAdd) {
        event.preventDefault();
        this.submitProduct({ checkout: false, sourceButton: stickyAdd });
        return;
      }

      const zoomTrigger = event.target.closest('[data-cahve-np-zoom-trigger]');
      if (zoomTrigger && this.gallery?.dataset.zoomMode === 'click') {
        event.preventDefault();
        this.openZoom(zoomTrigger.closest('[data-cahve-np-media-item]'));
        return;
      }

      if (event.target.closest('[data-cahve-np-lightbox-close]') || event.target.closest('[data-cahve-np-lightbox-backdrop]')) {
        event.preventDefault();
        this.closeZoom();
        return;
      }

      if (event.target.closest('[data-cahve-np-lightbox-prev]')) {
        event.preventDefault();
        this.showLightboxMedia(this.lightboxIndex - 1);
        return;
      }

      if (event.target.closest('[data-cahve-np-lightbox-next]')) {
        event.preventDefault();
        this.showLightboxMedia(this.lightboxIndex + 1);
        return;
      }

      const popupOpen = event.target.closest('[data-cahve-np-popup-open]');
      if (popupOpen) {
        event.preventDefault();
        const popup = this.querySelector(`#${CSS.escape(popupOpen.dataset.cahveNpPopupOpen)}`);
        this.openPopup(popup, popupOpen);
        return;
      }

      if (event.target.closest('[data-cahve-np-popup-close]') || event.target.closest('[data-cahve-np-popup-backdrop]')) {
        event.preventDefault();
        this.closePopup(event.target.closest('[data-cahve-np-popup]'));
      }
    }

    handleChange(event) {
      const select = event.target.closest('[data-cahve-np-option-select]');
      if (!select) return;
      this.selectOption(Number(select.dataset.optionPosition), select.value);
    }

    handleTouchStart(event) {
      if (!this.isCarouselMode() || event.touches.length !== 1) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    }

    handleTouchEnd(event) {
      if (!this.isCarouselMode() || this.touchStartX === null) return;
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      this.touchStartX = null;
      this.touchStartY = null;

      if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
      this.showMedia(this.currentMediaIndex + (deltaX < 0 ? 1 : -1));
    }

    showMedia(index) {
      if (!this.mediaItems.length) return;
      const normalized = (index + this.mediaItems.length) % this.mediaItems.length;
      this.currentMediaIndex = normalized;

      this.mediaItems.forEach((item, itemIndex) => {
        const active = itemIndex === normalized;
        item.classList.toggle('is-active', active);
        if (!active) {
          item.querySelectorAll('video').forEach((video) => video.pause());
        }
      });

      this.thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === normalized;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', String(active));
      });

      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === normalized);
      });

      if (!this.isCarouselMode()) {
        this.mediaItems[normalized]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    initSelectedOptions() {
      const positions = new Set();
      this.querySelectorAll('[data-option-position]').forEach((el) => positions.add(Number(el.dataset.optionPosition)));
      this.optionPositions = Array.from(positions).sort((a, b) => a - b);
      this.selectedOptions = this.optionPositions.map((position) => {
        const selectedButton = this.querySelector(
          `[data-cahve-np-option-button][data-option-position="${position}"].is-selected`
        );
        if (selectedButton) return selectedButton.dataset.optionValue;
        const select = this.querySelector(`select[data-option-position="${position}"]`);
        return select?.value || '';
      });
    }

    selectOption(position, value) {
      const index = this.optionPositions.indexOf(position);
      if (index < 0) return;
      this.selectedOptions[index] = value;
      this.syncOptionUI(position, value);
      this.updateVariantFromSelections();
    }

    syncOptionUI(position, value) {
      // Main form: a <fieldset data-option-position> wrapping option buttons.
      this.querySelectorAll(`fieldset[data-option-position="${position}"]`).forEach((fieldset) => {
        fieldset.querySelectorAll('[data-cahve-np-option-button]').forEach((candidate) => {
          const selected = candidate.dataset.optionValue === value;
          candidate.classList.toggle('is-selected', selected);
          candidate.setAttribute('aria-pressed', String(selected));
        });
        const valueLabel = fieldset.querySelector('[data-cahve-np-option-value]');
        if (valueLabel) valueLabel.textContent = value;
      });

      // Sticky bar: a plain <select data-option-position>.
      this.querySelectorAll(`select[data-option-position="${position}"]`).forEach((select) => {
        if (select.value !== value) select.value = value;
      });
    }

    getSelectedOptions() {
      return this.selectedOptions || [];
    }

    updateVariantFromSelections() {
      const variants = this.productData?.variants || [];
      const selectedOptions = this.getSelectedOptions();
      const variant = variants.find((candidate) => {
        if (!selectedOptions.length) return true;
        return candidate.options.every((option, index) => option === selectedOptions[index]);
      });

      this.currentVariant = variant || null;

      const variantInput = this.querySelector('[data-cahve-np-variant-input]');
      const price = this.querySelector('[data-cahve-np-price]');
      const comparePrice = this.querySelector('[data-cahve-np-compare-price]');
      const priceStatusPill = this.querySelector('[data-cahve-np-price-status]');
      const saveBadge = this.querySelector('[data-cahve-np-save-badge]');
      const stock = this.querySelector('[data-cahve-np-stock]');
      const stockText = this.querySelector('[data-cahve-np-stock-text]');
      const addButtons = Array.from(this.querySelectorAll('[data-cahve-np-add]'));
      const addLabels = Array.from(this.querySelectorAll('[data-cahve-np-add-label]'));
      const buyButton = this.querySelector('[data-cahve-np-buy-now]');
      const stickyPrice = this.querySelector('[data-cahve-np-sticky-price]');
      const stickyComparePrice = this.querySelector('[data-cahve-np-sticky-compare-price]');

      if (!variant) {
        if (variantInput) variantInput.value = '';
        if (priceStatusPill) priceStatusPill.hidden = true;
        addButtons.forEach((button) => (button.disabled = true));
        if (buyButton) buyButton.disabled = true;
        addLabels.forEach((label) => this.setRollLabelText(label, 'Unavailable'));
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (price) price.textContent = this.formatMoney(variant.price);

      const showCompare = Number(variant.compareAtPrice) > Number(variant.price);
      if (comparePrice) {
        comparePrice.hidden = !showCompare;
        comparePrice.textContent = showCompare ? this.formatMoney(variant.compareAtPrice) : '';
      }
      if (priceStatusPill) {
        const isSoldOut = !variant.available;
        let statusText = '';

        if (isSoldOut) {
          statusText = priceStatusPill.dataset.soldOutLabel || 'Sold out';
        } else if (showCompare) {
          const savings = Number(variant.compareAtPrice) - Number(variant.price);

          switch (priceStatusPill.dataset.saleFormat) {
            case 'amount':
              statusText = `Save ${this.formatMoney(savings)}`;
              break;
            case 'label':
              statusText = priceStatusPill.dataset.saleLabel || 'Sale';
              break;
            default: {
              const percent = Math.round((savings / Number(variant.compareAtPrice)) * 100);
              statusText = `Save ${percent}%`;
            }
          }
        }

        priceStatusPill.classList.toggle('is-sale', Boolean(statusText) && !isSoldOut);
        priceStatusPill.classList.toggle('is-sold-out', isSoldOut);
        priceStatusPill.textContent = statusText;
        priceStatusPill.hidden = !statusText;
      }
      if (saveBadge) {
        if (showCompare) {
          const percent = Math.round(
            ((Number(variant.compareAtPrice) - Number(variant.price)) / Number(variant.compareAtPrice)) * 100
          );
          saveBadge.textContent = `Save ${percent}%`;
          saveBadge.hidden = false;
        } else {
          saveBadge.hidden = true;
          saveBadge.textContent = '';
        }
      }

      if (stock) {
        stock.classList.toggle('is-unavailable', !variant.available);
        const threshold = Number(stock.dataset.threshold || 5);
        const showUrgency =
          variant.available &&
          variant.inventoryManagement &&
          Number(variant.inventoryQuantity) > 0 &&
          Number(variant.inventoryQuantity) <= threshold;

        if (stockText) {
          if (!variant.available) {
            stockText.textContent = 'Out of stock';
          } else if (showUrgency) {
            stockText.textContent = `Only ${variant.inventoryQuantity} left`;
          } else {
            stockText.textContent = 'In stock';
          }
        }
      }

      addButtons.forEach((button) => (button.disabled = !variant.available));
      if (buyButton) buyButton.disabled = !variant.available;
      addLabels.forEach((label) => this.setRollLabelText(label, variant.available ? 'Add to cart' : 'Sold out'));
      if (stickyPrice) stickyPrice.textContent = this.formatMoney(variant.price);
      if (stickyComparePrice) {
        stickyComparePrice.hidden = !showCompare;
        stickyComparePrice.textContent = showCompare ? this.formatMoney(variant.compareAtPrice) : '';
      }

      if (variant.featuredMediaId) {
        const mediaIndex = this.mediaItems.findIndex(
          (item) => Number(item.dataset.mediaId) === Number(variant.featuredMediaId)
        );
        if (mediaIndex >= 0) this.showMedia(mediaIndex);
      }
    }

    // A plain `el.textContent = text` would destroy the two-span
    // `.cahve-roll-label__text` / `--clone` structure the hover-roll
    // animation depends on, permanently flattening it to static text after
    // the first update. Update the inner spans instead so the roll effect
    // survives every dynamic label change (variant switch, add-to-cart
    // states, etc).
    setRollLabelText(el, text) {
      if (!el) return;
      const rollSpans = el.querySelectorAll('.cahve-roll-label__text');
      if (rollSpans.length) {
        rollSpans.forEach((span) => (span.textContent = text));
      } else {
        el.textContent = text;
      }
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
          style: 'currency',
          currency: this.currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (_error) {
        return `${this.currencyCode} ${amount.toFixed(2)}`;
      }
    }

    changeQuantity(delta) {
      const inputs = Array.from(this.querySelectorAll('[data-cahve-np-quantity-input]'));
      if (!inputs.length) return;
      const current = Math.max(1, Number.parseInt(inputs[0].value, 10) || 1);
      this.setQuantity(current + delta);
    }

    handleQuantityInput(event) {
      this.setQuantity(Number.parseInt(event.target.value, 10) || 1);
    }

    setQuantity(value) {
      const normalized = Math.max(1, value);
      const inputs = Array.from(this.querySelectorAll('[data-cahve-np-quantity-input]'));
      const formQuantity = this.querySelector('[data-cahve-np-form-quantity]');
      inputs.forEach((input) => {
        if (Number(input.value) !== normalized) input.value = String(normalized);
      });
      if (formQuantity) formQuantity.value = String(normalized);
    }

    syncQuantity() {
      const inputs = Array.from(this.querySelectorAll('[data-cahve-np-quantity-input]'));
      if (!inputs.length) return;
      this.setQuantity(Number.parseInt(inputs[0].value, 10) || 1);
    }

    handleAddToCart(event) {
      event.preventDefault();
      this.submitProduct({ checkout: false, sourceButton: event.submitter || this.querySelector('[data-cahve-np-add]') });
    }

    async submitProduct({ checkout, sourceButton }) {
      const form = this.querySelector('[data-cahve-np-form]');
      const addButtons = Array.from(this.querySelectorAll('[data-cahve-np-add]'));
      const addLabels = Array.from(this.querySelectorAll('[data-cahve-np-add-label]'));
      const buyButton = this.querySelector('[data-cahve-np-buy-now]');
      const actionButton = sourceButton || (checkout ? buyButton : addButtons[0]);

      if (!form || !actionButton || !this.currentVariant?.available || this.isSubmitting) return;

      this.isSubmitting = true;
      this.clearFormError();
      this.syncQuantity();
      actionButton.classList.add('is-loading');
      actionButton.setAttribute('aria-busy', 'true');
      addButtons.forEach((button) => (button.disabled = true));
      if (buyButton) buyButton.disabled = true;

      const formData = new FormData(form);
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartRenderer = cartDrawer || cartNotification;

      if (!checkout && cartRenderer?.getSectionsToRender) {
        formData.append(
          'sections',
          cartRenderer
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this item to the cart.');
        }

        if (checkout) {
          window.location.assign('/checkout');
          return;
        }

        addLabels.forEach((label) => this.setRollLabelText(label, 'Added'));
        actionButton.classList.remove('is-loading');
        actionButton.removeAttribute('aria-busy');

        if (cartRenderer?.renderContents && result.sections) {
          cartRenderer.renderContents(result);
        } else {
          document.dispatchEvent(new CustomEvent('cahve:cart-updated', { detail: { item: result, source: 'new-product' } }));
        }

        window.setTimeout(() => {
          if (this.currentVariant?.available) addLabels.forEach((label) => this.setRollLabelText(label, 'Add to cart'));
        }, 1600);
      } catch (error) {
        console.error('[Cahvé new product]', error);
        this.showFormError(error.message);
      } finally {
        this.isSubmitting = false;
        actionButton.classList.remove('is-loading');
        actionButton.removeAttribute('aria-busy');
        addButtons.forEach((button) => (button.disabled = !this.currentVariant?.available));
        if (buyButton) buyButton.disabled = !this.currentVariant?.available;
      }
    }

    async addBuyItWith(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || this.isBuyWithSubmitting) return;

      this.isBuyWithSubmitting = true;
      const label = button.querySelector('[data-cahve-np-buy-with-label]') || button;
      const originalLabel =
        label.querySelector('.cahve-roll-label__text:not(.cahve-roll-label__text--clone)')?.textContent.trim() ||
        label.textContent.trim();
      let addedSuccessfully = false;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setRollLabelText(label, 'Adding…');

      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartRenderer = cartDrawer || cartNotification;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (cartRenderer?.getSectionsToRender) {
        formData.append(
          'sections',
          cartRenderer
            .getSectionsToRender()
            .map((section) => section.id)
            .join(',')
        );
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to add this item to the cart.');
        }

        addedSuccessfully = true;
        this.setRollLabelText(label, 'Added');
        if (cartRenderer?.renderContents && result.sections) {
          cartRenderer.renderContents(result);
        } else {
          document.dispatchEvent(new CustomEvent('cahve:cart-updated', { detail: { item: result, source: 'new-product-buy-with' } }));
        }
      } catch (error) {
        console.error('[Cahvé new product]', error);
        this.setRollLabelText(label, originalLabel);
      } finally {
        this.isBuyWithSubmitting = false;
        button.removeAttribute('aria-busy');

        if (addedSuccessfully) {
          window.setTimeout(() => {
            this.setRollLabelText(label, originalLabel);
            button.disabled = false;
          }, 1600);
        } else {
          button.disabled = false;
        }
      }
    }

    toggleAccordion(trigger) {
      const item = trigger.closest('.cahve-np-accordion-item');
      const panel = item?.querySelector('[data-cahve-np-accordion-panel]');
      if (!panel) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        panel.style.maxHeight = `${panel.scrollHeight}px`;
        requestAnimationFrame(() => {
          panel.style.maxHeight = '0px';
        });
        trigger.setAttribute('aria-expanded', 'false');
        panel.addEventListener(
          'transitionend',
          () => {
            panel.hidden = true;
          },
          { once: true }
        );
      } else {
        panel.hidden = false;
        panel.style.maxHeight = '0px';
        requestAnimationFrame(() => {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
        });
        trigger.setAttribute('aria-expanded', 'true');
        panel.addEventListener(
          'transitionend',
          () => {
            panel.style.maxHeight = 'none';
          },
          { once: true }
        );
      }
    }

    showFormError(message) {
      const form = this.querySelector('[data-cahve-np-form]');
      if (!form) return;
      const error = document.createElement('p');
      error.className = 'cahve-np-error';
      error.setAttribute('role', 'alert');
      error.textContent = message;
      form.prepend(error);
    }

    clearFormError() {
      this.querySelector('.cahve-np-error')?.remove();
    }

    openZoom(mediaItem) {
      const index = this.mediaItems.indexOf(mediaItem);
      if (!this.lightbox || !this.lightboxImage || index < 0) return;
      this.lightboxIndex = index;
      this.showLightboxMedia(index);
      this.lightboxTrigger = document.activeElement;
      this.lightbox.hidden = false;
      requestAnimationFrame(() => this.lightbox.classList.add('is-open'));
      document.addEventListener('keydown', this.handleLightboxKeydown);
      this.lightboxBodyLockWasPresent = document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
    }

    showLightboxMedia(index) {
      const normalized = (index + this.mediaItems.length) % this.mediaItems.length;
      this.lightboxIndex = normalized;
      const img = this.mediaItems[normalized]?.querySelector('img');
      if (!img || !this.lightboxImage) return;
      this.lightboxImage.src = img.currentSrc || img.src;
      this.lightboxImage.alt = img.alt || '';
      this.resetLightboxZoom();
    }

    resetLightboxZoom() {
      this.lightboxPan = { x: 0, y: 0 };
      this.lightboxImage?.classList.remove('is-zoomed');
      this.applyLightboxPan();
    }

    applyLightboxPan() {
      if (!this.lightboxImage) return;
      const { x, y } = this.lightboxPan || { x: 0, y: 0 };
      this.lightboxImage.style.transform = this.lightboxImage.classList.contains('is-zoomed')
        ? `scale(1.9) translate(${x}px, ${y}px)`
        : '';
    }

    getLightboxPanBounds() {
      const viewport = this.lightbox?.querySelector('[data-cahve-np-lightbox-viewport]');
      if (!viewport || !this.lightboxImage) return { maxX: 0, maxY: 0 };
      const scale = 1.9;
      const imageRect = this.lightboxBaseRect || this.lightboxImage.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();
      const maxX = Math.max(0, (imageRect.width * scale - viewportRect.width) / 2 / scale);
      const maxY = Math.max(0, (imageRect.height * scale - viewportRect.height) / 2 / scale);
      return { maxX, maxY };
    }

    startLightboxPointer(event) {
      if (!this.lightboxImage) return;
      this.lightboxBaseRect = this.lightboxImage.classList.contains('is-zoomed')
        ? this.lightboxBaseRect
        : this.lightboxImage.getBoundingClientRect();
      this.lightboxPointer = {
        startClientX: event.clientX,
        startClientY: event.clientY,
        startPanX: this.lightboxPan?.x || 0,
        startPanY: this.lightboxPan?.y || 0,
        moved: 0,
        wasZoomed: this.lightboxImage.classList.contains('is-zoomed'),
      };
      this.lightboxImage.setPointerCapture?.(event.pointerId);
    }

    moveLightboxPointer(event) {
      if (!this.lightboxPointer || !this.lightboxImage?.classList.contains('is-zoomed')) return;
      const dx = (event.clientX - this.lightboxPointer.startClientX) / 1.9;
      const dy = (event.clientY - this.lightboxPointer.startClientY) / 1.9;
      this.lightboxPointer.moved = Math.max(this.lightboxPointer.moved, Math.abs(dx), Math.abs(dy));
      const bounds = this.getLightboxPanBounds();
      this.lightboxPan = {
        x: Math.min(bounds.maxX, Math.max(-bounds.maxX, this.lightboxPointer.startPanX + dx)),
        y: Math.min(bounds.maxY, Math.max(-bounds.maxY, this.lightboxPointer.startPanY + dy)),
      };
      this.applyLightboxPan();
    }

    endLightboxPointer(event) {
      const pointer = this.lightboxPointer;
      this.lightboxPointer = null;
      if (!pointer || !this.lightboxImage) return;

      const tap = pointer.moved < 6;
      if (!tap) return;

      if (!pointer.wasZoomed) {
        this.lightboxBaseRect = this.lightboxImage.getBoundingClientRect();
        this.lightboxImage.classList.add('is-zoomed');
        this.lightboxPan = { x: 0, y: 0 };
        this.applyLightboxPan();
      } else {
        this.resetLightboxZoom();
      }
    }

    closeZoom() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.classList.remove('is-open');
      document.removeEventListener('keydown', this.handleLightboxKeydown);
      if (!this.lightboxBodyLockWasPresent) document.body.classList.remove('overflow-hidden');
      window.setTimeout(() => {
        this.lightbox.hidden = true;
        this.resetLightboxZoom();
      }, 260);
      if (this.lightboxTrigger?.isConnected) this.lightboxTrigger.focus();
    }

    openPopup(popup, trigger) {
      if (!popup) return;
      this.popupTrigger = trigger || document.activeElement;
      popup.hidden = false;
      requestAnimationFrame(() => popup.classList.add('is-open'));
      document.addEventListener('keydown', this.handlePopupKeydown);
      popup.querySelector('[data-cahve-np-popup-close]')?.focus();
    }

    closePopup(popup) {
      if (!popup || popup.hidden) return;
      popup.classList.remove('is-open');
      document.removeEventListener('keydown', this.handlePopupKeydown);
      window.setTimeout(() => {
        popup.hidden = true;
      }, 260);
      if (this.popupTrigger?.isConnected) this.popupTrigger.focus();
    }

    initRevealObserver() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
        this.classList.add('is-visible');
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.classList.add('is-visible');
            observer.disconnect();
          });
        },
        { threshold: 0.1 }
      );
      observer.observe(this);
    }

    initTimers() {
      this.timers = Array.from(this.querySelectorAll('[data-cahve-np-timer]'));
      if (!this.timers.length) return;
      this.timers.forEach((timer) => this.tickTimer(timer));
      this.timerInterval = window.setInterval(() => {
        this.timers.forEach((timer) => this.tickTimer(timer));
      }, 1000);
    }

    tickTimer(timer) {
      const end = new Date(timer.dataset.end).getTime();
      const valueEl = timer.querySelector('[data-cahve-np-timer-value]');
      const expiredEl = timer.querySelector('[data-cahve-np-timer-expired]');
      if (Number.isNaN(end)) {
        timer.hidden = true;
        return;
      }

      const diff = end - Date.now();
      if (diff <= 0) {
        if (valueEl) valueEl.hidden = true;
        if (expiredEl) expiredEl.hidden = false;
        return;
      }

      const pad = (value) => String(value).padStart(2, '0');
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      if (valueEl) {
        valueEl.textContent =
          days > 0 ? `${days}d ${pad(hours)}h ${pad(minutes)}m` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
    }

    initComplementary() {
      this.querySelectorAll('[data-cahve-np-complementary]').forEach((block) => this.fetchComplementary(block));
    }

    scrollComplementary(control) {
      const block = control.closest('[data-cahve-np-complementary]');
      const track = block?.querySelector('[data-cahve-np-complementary-list]');
      const firstItem = track?.querySelector('.cahve-np-complementary__item');
      if (!track || !firstItem) return;

      const gap = Number.parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const distance = firstItem.getBoundingClientRect().width + gap;
      const direction = control.dataset.cahveNpComplementaryScroll === 'next' ? 1 : -1;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      track.scrollBy({
        left: direction * distance,
        behavior: reducedMotion ? 'auto' : 'smooth',
      });
    }

    updateComplementaryCarouselState(block) {
      const track = block?.querySelector('[data-cahve-np-complementary-list]');
      const previousButton = block?.querySelector('[data-cahve-np-complementary-scroll="prev"]');
      const nextButton = block?.querySelector('[data-cahve-np-complementary-scroll="next"]');
      if (!track || !previousButton || !nextButton) return;

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previousButton.disabled = track.scrollLeft <= 4;
      nextButton.disabled = maxScroll <= 4 || track.scrollLeft >= maxScroll - 4;
    }

    initStickyAtc() {
      this.stickyAtc = this.querySelector('[data-cahve-np-sticky-atc]');
      if (!this.stickyAtc) return;

      this.initStickyAtcFooterClearance();

      if (this.stickyAtc.dataset.showMode === 'always') {
        this.stickyAtc.classList.add('is-visible');
        return;
      }

      const referenceButton = this.querySelector('[data-cahve-np-form] [data-cahve-np-add]');
      if (!referenceButton || !('IntersectionObserver' in window)) {
        this.stickyAtc.classList.add('is-visible');
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.stickyAtc.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      observer.observe(referenceButton);
    }

    initStickyAtcFooterClearance() {
      if (this.stickyAtc.dataset.position !== 'bottom') return;

      const footer = document.querySelector('footer.footer');
      if (!footer) return;

      const updateClearance = () => {
        const height = Math.ceil(this.stickyAtc.getBoundingClientRect().height);
        if (height <= 0) return;
        footer.style.setProperty('--cahve-sticky-atc-clearance', `${height}px`);
        footer.classList.add('cahve-footer--sticky-atc-clearance');
      };

      updateClearance();
      if ('ResizeObserver' in window) {
        this.stickyAtcResizeObserver = new ResizeObserver(updateClearance);
        this.stickyAtcResizeObserver.observe(this.stickyAtc);
      }
    }

    async fetchComplementary(block) {
      const url = block.dataset.url;
      const blockId = block.dataset.blockId;
      if (!url) return;

      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const markup = await response.text();
        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const fresh = parsed.querySelector(`[data-cahve-np-complementary][data-block-id="${blockId}"]`);

        if (fresh && fresh.textContent.trim()) {
          block.innerHTML = fresh.innerHTML;
          window.requestAnimationFrame(() => this.updateComplementaryCarouselState(block));
        } else {
          block.hidden = true;
        }
      } catch (error) {
        console.error('[Cahvé new product] Complementary products fetch failed.', error);
      }
    }
  }

  if (!customElements.get('cahve-new-product')) {
    customElements.define('cahve-new-product', CahveNewProduct);
  }
})();
